/*
  Ad slot with automatic network selection.

  Priority (first match wins):
    1. Custom HTML    — admin pasted raw HTML in /_vh-admin (takes precedence)
    2. Google AdSense — if adsense_client_id + this format's slot ID are set
    3. EthicalAds     — if ethicalads_id is set
    4. Dev placeholder

  All config resolved via lib/runtimeConfig.js — DB (admin panel) beats env.
*/
import { useEffect, useRef } from "react";
import { Megaphone } from "lucide-react";
import { loadAd, ethicalAdsAvailable } from "../lib/ethicalads";
import { adsenseAvailable, getAdsenseSlot, pushAdSenseSlot } from "../lib/adsense";
import { getConfig } from "../lib/runtimeConfig";

const FORMATS = {
  "in-feed":        { label: "Sponsored · 300×250", width: 300, height: 250, className: "mx-auto w-full max-w-[300px]", eaType: "image",  customKey: "in_feed" },
  "desktop-inline": { label: "Sponsored · 728×90",  width: 728, height: 90,  className: "w-full max-w-[728px] mx-auto", eaType: "text",   customKey: "desktop_inline" },
  "sidebar":        { label: "Sponsored · 300×600", width: 300, height: 600, className: "w-full max-w-[300px]",          eaType: "image",  customKey: "sidebar" },
};

function AdSenseIns({ clientId, slotId, format, cfg }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) pushAdSenseSlot(ref.current);
  }, []);
  return (
    <ins
      ref={ref}
      className="adsbygoogle block"
      style={{ display: "block", width: "100%", minHeight: `${cfg.height}px` }}
      data-ad-client={clientId}
      data-ad-slot={slotId}
      data-ad-format={format === "desktop-inline" ? "horizontal" : "auto"}
      data-full-width-responsive="true"
      data-testid={`adsense-${format}`}
    />
  );
}

function EthicalAdsPlacement({ pubId, format, cfg }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) loadAd(ref.current);
  }, []);
  return (
    <div
      ref={ref}
      data-ea-publisher={pubId}
      data-ea-type={cfg.eaType}
      data-ea-manual="true"
      id={`ea-${format}`}
      className="rounded-xl border overflow-hidden"
      style={{
        borderColor: "var(--border-subtle)",
        background: "var(--surface)",
        minHeight: format === "desktop-inline" ? "90px" : `${Math.min(cfg.height, 300)}px`,
      }}
    />
  );
}

function CustomHtmlAd({ html, format, cfg }) {
  // Sandbox the custom HTML in an iframe so any injected <script> can't touch
  // the host app (protects against XSS via admin credential compromise).
  const srcDoc = `<!doctype html><html><head><meta charset="utf-8"><style>
    html,body{margin:0;padding:0;background:transparent;font-family:-apple-system,system-ui,sans-serif}
    a{color:inherit;text-decoration:none}
    img{max-width:100%;height:auto;display:block}
    * { box-sizing: border-box; }
  </style></head><body>${html}</body></html>`;
  return (
    <iframe
      title="Sponsored"
      srcDoc={srcDoc}
      sandbox="allow-popups allow-popups-to-escape-sandbox"
      className="w-full rounded-xl border"
      style={{
        borderColor: "var(--border-subtle)",
        background: "var(--surface)",
        height: `${cfg.height}px`,
      }}
      data-testid={`custom-ad-${format}`}
    />
  );
}

export default function AdSlot({ format = "in-feed", testid, className = "" }) {
  const cfg = FORMATS[format] || FORMATS["in-feed"];
  const runtime = getConfig();
  const customHtml = (runtime.custom_ads || {})[cfg.customKey];

  const hasCustom = !!(customHtml && customHtml.trim());
  const useAdSense = !hasCustom && adsenseAvailable() && !!getAdsenseSlot(format);
  const useEthicalAds = !hasCustom && !useAdSense && ethicalAdsAvailable();

  const network = hasCustom ? "custom" : useAdSense ? "adsense" : useEthicalAds ? "ethicalads" : "none";

  return (
    <div
      className={`${cfg.className} ${className}`}
      data-ad-slot={format}
      data-ad-network={network}
      data-testid={testid || `ad-slot-${format}`}
      role="complementary"
      aria-label="Sponsored content"
    >
      <div className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>
        <Megaphone className="h-3 w-3" /> <span>Sponsored</span>
      </div>

      {hasCustom ? (
        <CustomHtmlAd html={customHtml} format={format} cfg={cfg} />
      ) : useAdSense ? (
        <AdSenseIns clientId={runtime.adsense_client_id} slotId={getAdsenseSlot(format)} format={format} cfg={cfg} />
      ) : useEthicalAds ? (
        <EthicalAdsPlacement pubId={runtime.ethicalads_id} format={format} cfg={cfg} />
      ) : (
        <div
          className="rounded-xl border border-dashed flex flex-col items-center justify-center text-center px-4"
          style={{
            borderColor: "color-mix(in srgb, var(--border-subtle) 80%, var(--text-muted))",
            background: "color-mix(in srgb, var(--surface-2) 60%, transparent)",
            minHeight: `${cfg.height}px`,
          }}
        >
          <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{cfg.label}</div>
          <div className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>
            Configure via <code>/_vh-admin</code> or env vars
          </div>
        </div>
      )}
    </div>
  );
}
